import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "services/NotificationService";
import { getErrorMessage } from "services/UtilityService";
import DynamicSwitchOptionItemTypeField from "../../../commons/fields/DynamicSwitchOptionItemTypeField";
import { getValidItemType } from "./NewDrug.action";

// drugDsoId
const NewDrugRestrictedItemTypeField = (props) => {
  const {
    drugDsoId: propDrugDsoId,
    savedValue,
    updatedValue,
    values,
    handleBlur,
    isVisible,
    isLoadingValidCases,
    setIsLoadingValidCases,
  } = props;

  const [drugDsoId, setDrugDsoId] = useState(null);
  const [validItemTypes, setValidItemTypes] = useState([]);

  const dispatch = useDispatch();

  const {
    workingCodexVersionId,
    dynamicSwitchGroup,
    medDrugs,
    error,
    isLoading,
  } = useSelector((state) => ({
    workingCodexVersionId: state.codexVersion
      ? state.codexVersion.codexVersionId
      : null,
    dynamicSwitchGroup: state.dynamicSwitchGroupDetails
      ? state.dynamicSwitchGroupDetails.dynamicSwitchGroup
      : null,
    medDrugs: state.newDrug.medDrugs,
    error: state.newDrug.error,
    isLoading: state.newDrug.isLoading,
  }));

  const getAllItemTypes = useCallback((drugs) => {
    let itemTypesArray = [];
    let shouldAddBoth = true;
    drugs.forEach((medDrug) => {
      const { canAddDrug, drugValidityInfoList, itemType } = medDrug;
      if (
        canAddDrug &&
        (!drugValidityInfoList || drugValidityInfoList.length == 0)
      ) {
        itemTypesArray.push({
          canAdd: true,
          itemType: itemType,
          reason: null,
          additionalInfo: "",
        });
      } else if (!canAddDrug) {
        // if same drug exists with same item type and same group
        itemTypesArray.push({
          canAdd: false,
          itemType: itemType,
          reason: `Cannot add this drug as ${itemType} in this group because it already exists`,
          additionalInfo: "",
        });
        shouldAddBoth = false;
      } else {
        let additionalInfo = [];
        for (let i = 0; i < drugValidityInfoList.length; i++) {
          let medNames = drugValidityInfoList[i].medNames.reduce(
            (previousValue, currentValue) =>
              previousValue == ""
                ? currentValue
                : previousValue + " , " + currentValue,
            ""
          );
          additionalInfo += `    - In ${drugValidityInfoList[i].dynamicSwitchGroupName} group, under ${medNames} drugs \n`;
        }
        itemTypesArray.push({
          canAdd: true,
          itemType: itemType,
          reason: `These strenghts are already active in following DSGs:`,
          additionalInfo: additionalInfo,
        });
      }
    });
    if (shouldAddBoth) {
      itemTypesArray.push({
        canAdd: true,
        itemType: "both",
        reason: null,
        additionalInfo: "",
      });
    }
    return itemTypesArray;
  }, []);

  // Handle drugDsoId changes
  useEffect(() => {
    if (propDrugDsoId !== drugDsoId) {
      setValidItemTypes([]);
      const nextIsLoadingValidCases = !!propDrugDsoId;
      setIsLoadingValidCases(nextIsLoadingValidCases);
      setDrugDsoId(propDrugDsoId || null);
    }
  }, [propDrugDsoId, drugDsoId, setIsLoadingValidCases]);

  // Fire getValidItemType when drugDsoId becomes set
  useEffect(() => {
    if (drugDsoId != null) {
      const payload = [
        {
          drugDsoId,
          itemType: "target",
        },
        {
          drugDsoId,
          itemType: "switch",
        },
        {
          drugDsoId,
          itemType: "auxiliary",
        },
      ];
      if (workingCodexVersionId && dynamicSwitchGroup) {
        dispatch(
          getValidItemType(
            workingCodexVersionId,
            dynamicSwitchGroup.dynamicSwitchGroupId,
            payload
          )
        );
      }
    }
  }, [drugDsoId, workingCodexVersionId, dynamicSwitchGroup, dispatch]);

  // React to medDrugs / loading / error similar to getDerivedStateFromProps
  useEffect(() => {
    if (
      medDrugs?.length > 0 &&
      medDrugs[0].drugDsoId == propDrugDsoId &&
      !isLoading &&
      isLoadingValidCases
    ) {
      setIsLoadingValidCases(false);
      if (error) {
        showNotification({
          title: "Could not get required information",
          message:
            getErrorMessage(error) ||
            "Something went wrong. Please try again later",
          position: "tr",
          type: "error",
        });
        setValidItemTypes([]);
      } else {
        const validTypes = getAllItemTypes(medDrugs);
        setValidItemTypes(validTypes);
      }
    }
  }, [
    medDrugs,
    propDrugDsoId,
    isLoading,
    isLoadingValidCases,
    setIsLoadingValidCases,
    error,
    getAllItemTypes,
  ]);

  const { itemTypes, errors, warnings } = useMemo(() => {
    let itemTypes = [];
    let errors = "";
    let warnings = "";
    validItemTypes.forEach((x) => {
      if (x.canAdd) {
        itemTypes.push(x.itemType);
        if (x.reason) {
          warnings += `Adding this drug as ${x.itemType} can have following implications:\n`;
          warnings += `  1. ${x.reason}`;
          warnings += "\n";
          warnings += `${x.additionalInfo}`;
        }
      } else {
        if (x.reason) {
          errors += x.reason + "\n";
        }
      }
    });
    return { itemTypes, errors, warnings };
  }, [validItemTypes]);

  const handleItemTypeChange = (e) => {
    values.itemType = e.target.value;
  };

  if (!isVisible) {
    return null;
  }

  return (
    <DynamicSwitchOptionItemTypeField
      dynamicSwitchOptionId={drugDsoId}
      key={`itemType${drugDsoId}`}
      isEditMode={true}
      savedValue={savedValue}
      updatedValue={updatedValue}
      handleChange={handleItemTypeChange}
      handleBlur={handleBlur}
      isDrugTypeBoth={false}
      isHorizontalView={true}
      error={errors}
      warning={warnings}
      isRequired={true}
      itemTypesToDisplay={itemTypes}
    />
  );
};

export default NewDrugRestrictedItemTypeField;
